import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { MapPin, Phone, Mail, ArrowUp, Clock } from 'lucide-react';

const Footer = () => {
  const { t, isRTL } = useLanguage();
  const currentYear = new Date().getFullYear();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const quickLinks = [
    { name: t('nav.home'), href: '#home' },
    { name: t('nav.about'), href: '#about' },
    { name: t('nav.services'), href: '#services' },
    { name: t('nav.products'), href: '#products' },
    { name: t('nav.whyUs'), href: '#why-us' },
    { name: t('nav.contact'), href: '#contact' },
  ];

  const services = [
    t('services.s1.title'),
    t('services.s2.title'),
    t('services.s3.title'),
    t('services.s4.title'),
    t('services.s5.title'),
    t('services.s6.title'),
  ];

  return (
    <footer className="bg-slate-900 text-slate-300 relative">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Company Info */}
          <div className="lg:col-span-1 space-y-6">
            <div className="flex items-center">
              <img 
                src="/images/logo.png" 
                alt="AFAQ ALNABTH Medical Supplies" 
                className="h-16 w-auto brightness-0 invert"
              />
            </div>
            <p className="text-sm text-slate-400 leading-relaxed">
              {t('footer.description')}
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-3 text-sm">
                <MapPin className="w-4 h-4 text-brand-red flex-shrink-0 mt-0.5" />
                <span>
                  {t('contact.address.line1')}<br />
                  {t('contact.address.line2')}<br />
                  {t('contact.address.line3')}
                </span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Phone className="w-4 h-4 text-brand-red flex-shrink-0" />
                <div className="flex flex-col" dir="ltr">
                  <a href="tel:+971542808859" className="hover:text-white transition-colors">+971 54 280 8859</a>
                  <a href="tel:+971502951774" className="hover:text-white transition-colors">+971 50 295 1774</a>
                </div>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Mail className="w-4 h-4 text-brand-red flex-shrink-0" />
                <a href="mailto:info@afaqalnabth.com" className="hover:text-white transition-colors" dir="ltr">
                  info@afaqalnabth.com
                </a>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-6">{t('footer.quickLinks')}</h4>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className={`text-sm text-slate-400 hover:text-brand-red transition-colors duration-200 ${isRTL ? 'text-right w-full' : 'text-left'}`}
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold mb-6">{t('footer.ourServices')}</h4>
            <ul className="space-y-3">
              {services.map((service, index) => (
                <li key={index}>
                  <button
                    onClick={() => scrollToSection('#services')}
                    className={`text-sm text-slate-400 hover:text-brand-red transition-colors duration-200 ${isRTL ? 'text-right w-full' : 'text-left'}`}
                  >
                    {service}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Business Hours */}
          <div>
            <h4 className="text-white font-semibold mb-6">{t('contact.hours')}</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex justify-between items-center">
                <span className="text-slate-400">{t('contact.monFri')}</span>
                <span className="text-slate-300" dir="ltr">9:00 AM – 6:00 PM</span>
              </li>
              <li className="flex justify-between items-center">
                <span className="text-slate-400">{t('contact.satSun')}</span>
                <span className="text-brand-red font-medium">{t('contact.closed')}</span>
              </li>
            </ul>
            <div className="mt-6 p-4 bg-slate-800 rounded-xl">
              <p className="text-xs text-slate-400 mb-2">{t('footer.urgent')}</p>
              <p className="text-sm text-brand-red font-medium">{t('footer.support')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-500">
              © {currentYear} {isRTL ? 'مؤسسة آفاق النبض للمستلزمات الطبية' : 'AFAQ ALNABTH MEDICAL SUPPLIES EST.'} {t('footer.rights')}
            </p>
            <button
              onClick={scrollToTop}
              className="flex items-center gap-2 text-sm text-slate-400 hover:text-brand-red transition-colors duration-200"
            >
              <span>{t('footer.backToTop')}</span>
              <ArrowUp className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
